/**
 * Timer V2 — allocation review (8E).
 *
 * ============================================================================
 * WHAT THIS DIALOG IS FOR
 * ============================================================================
 * Stopping work produces MEASURED time and a FROZEN PROPOSAL for how that time
 * divides across the jobs the technician selected. Neither is a decision. This
 * dialog is where a human makes the decision: look at the proposal, adjust it
 * if it is wrong, and accept a distribution. Only then does measured time
 * become accepted allocation.
 *
 * `TicketTimerControls` decides WHEN a review is needed. This component owns
 * HOW one frozen proposal is inspected and accepted, and nothing else.
 *
 * ============================================================================
 * THREE THINGS IT DELIBERATELY DOES NOT DO
 * ============================================================================
 * 1. It never re-derives the proposal. Everything shown comes from
 *    `get_my_timer_episode_review`, both immediately after Stop and after a
 *    refresh. One source, one parser, one authorization path — so the recovery
 *    path is the path that is always exercised.
 *
 * 2. It never completes anything. No job is finished, no ticket is closed, no
 *    custody moves. `p_complete_job_ids` is always empty. Accepting an
 *    allocation says "this is how my measured time divides" and stops there.
 *
 * 3. It never spreads unattributed time. If 100 minutes were measured but only
 *    85 are tied to selected jobs, the editor totals to 85. The remaining 15
 *    stay explicitly ticket-level. Quietly dividing them across jobs would
 *    make the UI arithmetic tidier and the record false.
 *
 * ============================================================================
 * WHY A LOST RESPONSE IS NOT A FAILURE
 * ============================================================================
 * If Accept reaches the server, commits, and the response is lost, the write
 * happened. Reporting that as an error is how a technician resubmits, or
 * concludes their time was thrown away. So a failed submit keeps the dialog
 * open with Retry enabled, and a retry that comes back ALREADY_RESOLVED is
 * treated as success — because it is one. The backend's idempotency (8D) is
 * what makes that safe; this component is the half that has to believe it.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, RotateCcw, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  readTimerEpisodeReview,
  finalizeTimerEpisodeReview,
  isReviewSettled,
  type TimerEpisodeReview,
  type TimerReviewJob,
  type TimerReviewOutcome,
} from '@/utils/timerEpisodeReview';

interface TimerAllocationReviewDialogProps {
  workEpisodeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * "Server truth changed, or contradicted what you cached. Re-read it."
   *
   * 8E called this `onReviewed` and fired it only after a submit. That name
   * stopped being true in 8E.1: the READER can also prove the parent's cached
   * state wrong — a colleague or manager resolves the episode from another
   * device, and the parent is still rendering Review Time for work that no
   * longer needs it. Firing a callback called `onReviewed` for "somebody else
   * already reviewed this" would be a lie, and firing nothing was the dead end
   * this phase closes.
   *
   * The parent's obligation is the same in both cases and is deliberately not
   * described here: re-read canonical state and render whatever it says. This
   * callback never asserts what the new state IS.
   */
  onServerStateChanged: () => void;
}

/**
 * Reader outcomes that contradict the reason the parent opened this dialog.
 * Each one means the parent's cached `AWAITING_REVIEW` is stale.
 *
 * Deliberately EXCLUDED:
 *
 *   NO_REVIEW_SNAPSHOT / INTEGRITY_ERROR — repair states. The work still needs
 *     a review it cannot have; converging here would let the technician escape
 *     a blocked state into a cheerful IDLE and lose the problem.
 *   READ_FAILED — transport failure. It says nothing about the episode, and
 *     treating a dropped request as lifecycle truth is how a network blip
 *     silently clears somebody's outstanding work.
 */
const STALE_PARENT_OUTCOMES: TimerReviewOutcome[] = [
  'ALREADY_RESOLVED', 'NOT_AWAITING_REVIEW', 'EPISODE_NOT_AVAILABLE',
];

/** What the technician has typed, keyed by job id. Raw strings so an empty
 *  field stays empty rather than snapping to 0 under the cursor. */
type Draft = Record<string, string>;

const draftFromSuggested = (jobs: TimerReviewJob[]): Draft =>
  Object.fromEntries(jobs.map((j) => [j.jobId, String(j.suggestedMinutes)]));

/** Whole, non-negative minutes only. The server is the authority; this is UX. */
function parseMinutes(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/**
 * Where a job's suggested minutes came from — EVERY non-zero component.
 *
 * 8E printed a single label per job and picked it by priority. That was lossy
 * and, in one case, false:
 *
 * · LOSSY. `derive_episode_allocation` sums each component ACROSS SEGMENTS. A
 *   technician who works Pump alone, then edits scope to Pump + Axle, ends the
 *   episode with Pump holding both direct AND split minutes — measured on the
 *   local database as `direct 20 / proportional 40`. The old UI showed only
 *   "Split by expected time" and silently hid the 20 minutes that were timed
 *   against Pump and nothing else.
 *
 * · FALSE. Any `fallback_minutes > 0` was labelled "Even split". Fallback also
 *   fires when SOME jobs have an expected-time basis and others do not: the
 *   algorithm substitutes the mean of the real weights for the missing ones
 *   and marks the whole segment as fallback. Measured with bases 10 / 30 / 0,
 *   the fallback shares came out 10 / 30 / 20 — not equal. The frozen review
 *   records only `fallback_minutes`, so it cannot distinguish a genuinely
 *   equal split from a substituted-weight one, and the UI must not claim the
 *   stronger of the two.
 *
 * These are EXPLANATION only. Nothing here is editable and nothing is
 * recomputed in the browser — the numbers are read straight from the frozen
 * review.
 */
interface ProvenancePart {
  label: string;
  minutes: number;
}

function provenanceParts(job: TimerReviewJob): ProvenancePart[] {
  const parts: ProvenancePart[] = [];
  if (job.directMinutes > 0) {
    parts.push({ label: 'Direct', minutes: job.directMinutes });
  }
  if (job.autoProportionalMinutes > 0) {
    parts.push({ label: 'Expected-time split', minutes: job.autoProportionalMinutes });
  }
  if (job.fallbackMinutes > 0) {
    // Deliberately NOT "Even split" — see above.
    parts.push({ label: 'Fallback allocation', minutes: job.fallbackMinutes });
  }
  return parts;
}

export function TimerAllocationReviewDialog({
  workEpisodeId,
  open,
  onOpenChange,
  onServerStateChanged,
}: TimerAllocationReviewDialogProps) {
  const [review, setReview] = useState<TimerEpisodeReview | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Set when a submit produced no server answer. The write may have landed. */
  const [submitUncertain, setSubmitUncertain] = useState(false);

  /**
   * The callback is held in a ref, NOT closed over by `load`.
   *
   * This is the loop guard. If `load` depended on the callback, and the parent
   * passes a fresh arrow each render, then: load -> notify -> parent re-render
   * -> new callback identity -> new `load` identity -> the effect below refires
   * -> load -> notify -> forever. Keeping the callback out of the dependency
   * array makes that impossible by construction rather than by discipline.
   */
  const onServerStateChangedRef = useRef(onServerStateChanged);
  useEffect(() => { onServerStateChangedRef.current = onServerStateChanged; });

  /** `episode:outcome` already notified, so one load notifies at most once. */
  const notifiedRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!workEpisodeId) return;
    setIsLoading(true);
    setSubmitUncertain(false);
    const r = await readTimerEpisodeReview(workEpisodeId);
    setReview(r);
    // The frozen proposal is the starting point — not zero, not book time, not
    // whatever was typed last time the dialog was open.
    setDraft(r.outcome === 'AVAILABLE' ? draftFromSuggested(r.jobs) : {});
    setIsLoading(false);

    // The reader has just proved the parent's cached state wrong. Tell it, so
    // the stale Review Time control cannot survive the dialog it opened.
    if (STALE_PARENT_OUTCOMES.includes(r.outcome)) {
      const key = `${workEpisodeId}:${r.outcome}`;
      if (notifiedRef.current !== key) {
        notifiedRef.current = key;
        onServerStateChangedRef.current();
      }
    }
  }, [workEpisodeId]);

  // Reload on every open. A draft abandoned earlier is not evidence and must
  // not survive; the server's frozen proposal is what the technician sees.
  useEffect(() => {
    if (open && workEpisodeId) void load();
    if (!open) {
      setReview(null);
      setDraft({});
      setSubmitUncertain(false);
      notifiedRef.current = null;
    }
  }, [open, workEpisodeId, load]);

  const parsed = useMemo(
    () => (review?.jobs ?? []).map((j) => ({ job: j, value: parseMinutes(draft[j.jobId] ?? '') })),
    [review, draft],
  );
  const allValid = parsed.every((p) => p.value !== null);
  const allocated = parsed.reduce((sum, p) => sum + (p.value ?? 0), 0);
  const target = review?.attributableMinutes ?? 0;
  const conserves = allValid && allocated === target;
  const canAccept = review?.outcome === 'AVAILABLE' && conserves && !isSubmitting;

  const handleAccept = useCallback(async () => {
    if (!review || !workEpisodeId) return;
    setIsSubmitting(true);
    // The COMPLETE distribution, stated explicitly — including anything left at
    // zero. Accepting an unedited proposal is still a human acceptance, so it
    // is submitted as values rather than as "no opinion".
    const allocations = review.jobs.map((j) => ({
      job_id: j.jobId,
      final_minutes: parseMinutes(draft[j.jobId] ?? '') ?? 0,
    }));

    const result = await finalizeTimerEpisodeReview(workEpisodeId, allocations);
    setIsSubmitting(false);

    if (isReviewSettled(result.outcome)) {
      toast.success(
        result.outcome === 'RESOLVED' ? 'Time allocation accepted' : 'This time was already reviewed',
      );
      onOpenChange(false);
      onServerStateChanged();
      return;
    }

    if (result.outcome === 'SUBMIT_FAILED') {
      // Do NOT close, and do NOT claim nothing was saved — it may have been.
      setSubmitUncertain(true);
      toast.error('Could not confirm the result. Press Retry.');
      return;
    }

    // Any other outcome is the server telling us the episode moved on.
    toast.info('This work is no longer awaiting review.');
    onOpenChange(false);
    onServerStateChanged();
  }, [review, workEpisodeId, draft, onOpenChange, onServerStateChanged]);

  const body = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"
             data-testid="timer-review-loading">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading time review…
        </div>
      );
    }
    if (!review) return null;

    if (review.outcome === 'ALREADY_RESOLVED') {
      return (
        <p className="py-6 text-sm" data-testid="timer-review-already-resolved">
          This work has already been reviewed. Nothing further is needed.
        </p>
      );
    }
    if (review.outcome === 'NOT_AWAITING_REVIEW') {
      return (
        <p className="py-6 text-sm" data-testid="timer-review-not-awaiting">
          This work is no longer awaiting a time review.
        </p>
      );
    }
    if (review.outcome === 'EPISODE_NOT_AVAILABLE') {
      // Generic on purpose. Anything more specific would confirm whether the
      // episode exists and who it belongs to.
      return (
        <p className="py-6 text-sm" data-testid="timer-review-unavailable">
          This time review is not available.
        </p>
      );
    }
    if (review.outcome === 'NO_REVIEW_SNAPSHOT' || review.outcome === 'INTEGRITY_ERROR') {
      return (
        <div className="flex items-start gap-2 py-6 text-sm" data-testid="timer-review-needs-attention">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <span>
            This time review needs attention and cannot be edited. Please contact a manager.
          </span>
        </div>
      );
    }
    if (review.outcome === 'READ_FAILED') {
      return (
        <div className="py-6 text-sm" data-testid="timer-review-load-error">
          <p className="mb-3">Could not load this time review.</p>
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      );
    }

    // ── AVAILABLE ─────────────────────────────────────────────────────────
    const hasUnattributed = review.unattributedMinutes > 0;
    return (
      <div className="space-y-4" data-testid="timer-review-editor">
        <div className="rounded-md bg-muted/50 p-3 text-sm" data-testid="timer-review-summary">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Measured time</span>
            <span data-testid="timer-review-measured">{review.measuredMinutes} min</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Time tied to selected jobs</span>
            <span data-testid="timer-review-attributable">{review.attributableMinutes} min</span>
          </div>
          {hasUnattributed && (
            <>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unattributed time</span>
                <span data-testid="timer-review-unattributed">
                  {review.unattributedMinutes} min
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Unattributed time stays on the ticket. It is not assigned to any job here.
              </p>
            </>
          )}
        </div>

        {review.jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="timer-review-no-jobs">
            No job time to allocate for this work.
          </p>
        ) : (
          <div className="space-y-3">
            {review.jobs.map((job) => {
              const raw = draft[job.jobId] ?? '';
              const invalid = parseMinutes(raw) === null;
              return (
                <div key={job.jobId} className="space-y-1" data-testid={`timer-review-job-${job.jobId}`}>
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor={`alloc-${job.jobId}`} className="text-sm font-medium">
                      {job.jobName}
                    </Label>
                    <Input
                      id={`alloc-${job.jobId}`}
                      data-testid={`timer-review-input-${job.jobId}`}
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      className="h-8 w-24 text-right"
                      aria-invalid={invalid}
                      value={raw}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, [job.jobId]: e.target.value }))
                      }
                    />
                  </div>
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid={`timer-review-provenance-${job.jobId}`}
                  >
                    Suggested {job.suggestedMinutes} min
                    {provenanceParts(job).length === 0
                      ? ' · No measured time'
                      : provenanceParts(job).map((p) => (
                          <span key={p.label}> · {p.label} {p.minutes}</span>
                        ))}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-3 text-sm">
          <span data-testid="timer-review-allocated">
            Allocated: {allocated} / {target} min
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="gap-1"
            data-testid="timer-review-reset"
            onClick={() => setDraft(draftFromSuggested(review.jobs))}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to suggested
          </Button>
        </div>

        {!allValid && (
          <p className="text-xs text-destructive" data-testid="timer-review-invalid">
            Enter whole minutes (0 or more) for every job.
          </p>
        )}
        {allValid && allocated < target && (
          <p className="text-xs text-destructive" data-testid="timer-review-under">
            {target - allocated} min still need allocation.
          </p>
        )}
        {allValid && allocated > target && (
          <p className="text-xs text-destructive" data-testid="timer-review-over">
            {allocated - target} min over.
          </p>
        )}
        {submitUncertain && (
          <p className="text-xs text-destructive" data-testid="timer-review-uncertain">
            The result could not be confirmed. Your time may already have been saved —
            press Retry to check.
          </p>
        )}
      </div>
    );
  };

  const showAccept = review?.outcome === 'AVAILABLE';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="timer-review-dialog">
        <DialogHeader>
          <DialogTitle>Review Time</DialogTitle>
          <DialogDescription>
            Confirm how your measured time is divided across the jobs you worked on.
          </DialogDescription>
        </DialogHeader>

        {body()}

        <DialogFooter>
          {/* Closing writes nothing. The episode stays awaiting review and can
              be reopened from the ticket at any time. */}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          {showAccept && (
            <Button
              onClick={() => void handleAccept()}
              disabled={!canAccept}
              data-testid="timer-review-accept"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitUncertain ? 'Retry' : 'Accept Time Allocation'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
